import React, { useState } from "react";
import { Artwork } from "../types/artwork";
import "./StatisticsChart.css";

interface StatisticsChartProps {
  stats: {
    pending: number;
    inProgress: number;
    digitized: number;
    obtained: number;
  };
  onFilterChange?: (filter: string) => void;
}

interface ChartColumn {
  key: string;
  label: string;
  value: number;
  color: string;
  icon: string;
  tooltip: {
    title: string;
    description: string;
  };
}

const StatisticsChart: React.FC<StatisticsChartProps> = ({ stats, onFilterChange }) => {
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  const columns: ChartColumn[] = [
    {
      key: "pending",
      label: "Pending",
      value: stats.pending,
      color: "#ffc107",
      icon: "📋",
      tooltip: {
        title: "📋 Pending",
        description: "Opere identificate ma non ancora ottenute. Contatto con l'istituzione da iniziare.",
      },
    },
    {
      key: "inProgress",
      label: "In Progress",
      value: stats.inProgress,
      color: "#17a2b8",
      icon: "⚡",
      tooltip: {
        title: "⚡ In Progress",
        description: "Acquisizione in corso. Trattativa o richiesta attiva con l'istituzione.",
      },
    },
    {
      key: "digitized",
      label: "Digitized",
      value: stats.digitized,
      color: "#28a745",
      icon: "✅",
      tooltip: {
        title: "✅ Digitized",
        description: "Opere digitalizzate ad alta qualità dal nostro team. Include rielaborazione professionale.",
      },
    },
    {
      key: "obtained",
      label: "Obtained",
      value: stats.obtained,
      color: "#6f42c1",
      icon: "🎯",
      tooltip: {
        title: "🎯 Obtained",
        description: "Opere ottenute dall'istituzione (include anche quelle digitalizzate da noi).",
      },
    },
  ];

  const maxValue = Math.max(...columns.map((col) => col.value));
  const total = stats.pending + stats.inProgress + stats.obtained;

  const getBarHeight = (value: number) => {
    if (maxValue === 0) return 0;
    return (value / maxValue) * 100;
  };

  const getPercentage = (value: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <div className="statistics-chart">
      <div className="statistics-chart-header">
        <h2 className="statistics-chart-title">
          <span className="statistics-chart-title-icon">📊</span>
          Statistiche Opere
        </h2>
        <p className="statistics-chart-subtitle">
          Panoramica dello stato di avanzamento della collezione
        </p>
      </div>

      <div className="statistics-chart-container">
        <div className="statistics-chart-grid">
          {columns.map((column) => {
            const barHeight = getBarHeight(column.value);
            const percentage = getPercentage(column.value);
            const isHovered = hoveredColumn === column.key;

            return (
              <div
                key={column.key}
                className={`statistics-chart-column ${isHovered ? "hovered" : ""}`}
                onMouseEnter={() => setHoveredColumn(column.key)}
                onMouseLeave={() => setHoveredColumn(null)}
                onClick={() => onFilterChange && onFilterChange(column.key === "digitized" ? "digitized" : column.key === "inProgress" ? "in-progress" : column.key)}
              >
                {/* Tooltip */}
                {isHovered && (
                  <div className="statistics-chart-tooltip">
                    <div className="statistics-chart-tooltip-header">
                      <h4 className="statistics-chart-tooltip-title">
                        {column.tooltip.title}
                      </h4>
                    </div>
                    <p className="statistics-chart-tooltip-description">
                      {column.tooltip.description}
                    </p>
                    <div className="statistics-chart-tooltip-stats">
                      <div className="statistics-chart-tooltip-stat">
                        <span className="statistics-chart-tooltip-stat-label">Totale:</span>
                        <span className="statistics-chart-tooltip-stat-value">{column.value}</span>
                      </div>
                      <div className="statistics-chart-tooltip-stat">
                        <span className="statistics-chart-tooltip-stat-label">Percentuale:</span>
                        <span className="statistics-chart-tooltip-stat-value">{percentage}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Icon */}
                <div className="statistics-chart-icon" style={{ color: column.color }}>
                  {column.icon}
                </div>

                {/* Bar Container */}
                <div className="statistics-chart-bar-container">
                  <div
                    className="statistics-chart-bar"
                    style={{
                      height: `${barHeight}%`,
                      background: `linear-gradient(180deg, ${column.color}, ${column.color}dd)`,
                      boxShadow: isHovered
                        ? `0 8px 25px ${column.color}66`
                        : `0 4px 15px ${column.color}33`,
                    }}
                  >
                    {/* Value inside bar */}
                    <div className="statistics-chart-bar-value">{column.value}</div>
                  </div>
                </div>

                {/* Label */}
                <div className="statistics-chart-label">
                  <span className="statistics-chart-label-text">{column.label}</span>
                  <span className="statistics-chart-label-percentage">{percentage}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="statistics-chart-info">
          <div className="statistics-chart-info-item">
            <span className="statistics-chart-info-icon">📚</span>
            <span className="statistics-chart-info-text">
              Totale opere: <strong>{total}</strong>
            </span>
          </div>
          <div className="statistics-chart-info-item">
            <span className="statistics-chart-info-icon">💡</span>
            <span className="statistics-chart-info-text">
              Clicca su una colonna per filtrare
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsChart;
